import { PARTNER_SUPPLIER_PERMISSIONS, PERMISSIONS } from '@/config/permissions'

const toolboxKeys: readonly string[] = Object.values(PERMISSIONS)

export interface PartnerSupplierRedirectUrls {
  /** The account app for this environment, where suppliers are sent. */
  accountAppUrl: string | undefined
  /** The public Reach site, where partners are sent. */
  reachSiteUrl: string
}

/**
 * Works out whether a signed-in user belongs outside Toolbox, and returns
 * the URL to send them to, or `null` to let them stay.
 *
 * Only a user with no Toolbox permission is sent away:
 * - a supplier goes to the account app
 * - a partner goes to the public Reach site
 * - a user with both keys is a supplier, so goes to the account app
 *
 * Anyone who also holds a Toolbox permission (for example staff testing in
 * the shared dev tenant) stays, and so does a user with no permissions.
 *
 * Returns `null` while the user has not loaded (`permissionKeys` is
 * `undefined`), so a slow session load never sends a staff member away. A
 * supplier also stays when no account app URL is configured, so a missing
 * env value cannot turn into a redirect to the current page.
 */
export function partnerSupplierRedirectTarget(
  permissionKeys: readonly string[] | undefined,
  urls: PartnerSupplierRedirectUrls,
): string | null {
  if (!permissionKeys) return null
  if (permissionKeys.some(key => toolboxKeys.includes(key))) return null
  if (permissionKeys.includes(PARTNER_SUPPLIER_PERMISSIONS.SUPPLIER)) {
    return urls.accountAppUrl || null
  }
  if (permissionKeys.includes(PARTNER_SUPPLIER_PERMISSIONS.PARTNER)) {
    return urls.reachSiteUrl
  }
  return null
}

/**
 * Reads the permission keys off a Frontegg user object. Returns `undefined`
 * when the user or their permission list has not loaded yet.
 */
export function permissionKeysOf(
  user: { permissions?: unknown } | null | undefined,
): string[] | undefined {
  const permissions = user?.permissions
  if (!Array.isArray(permissions)) return undefined
  return permissions
    .map((p: { key?: unknown } | null) => p?.key)
    .filter((key): key is string => typeof key === 'string')
}
